// /forgot-password — public. POSTs to Hono /auth/forgot-password which
// always 200s (never enumerates registered emails). On success we show a
// neutral "if an account exists, a reset link was sent" message so the
// UX matches the API's non-enumeration contract.

import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { ApiError } from '@/lib/api';
import { forgotPassword } from '@/lib/api';
import { BrandMark } from '@/components/Brand';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Request failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--bo-bg)] px-4 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-[color:var(--bo-line)] bg-white p-8 shadow-sm">
        {/* These pages are reached from an email link, where an unbranded
            page is exactly what a phishing warning looks like. */}
        <BrandMark />
        <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Reset your password</h1>
        <p className="mt-1 text-[13px] text-[color:var(--bo-mute)]">
          Enter your admin email — we&apos;ll send a reset link if the account exists.
        </p>

        {sent ? (
          <div className="mt-6 rounded-lg border border-[color:var(--bo-line)] bg-[#f0fdf4] p-4 text-[13px] text-[#166534]">
            If an account exists for <span className="font-medium">{email}</span>, a reset link was sent.
            The link is valid for 1 hour.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="block text-[12px] font-medium text-[color:var(--bo-ink)]">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[14px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </div>
            {error ? (
              <p className="rounded-md bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">{error}</p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-[color:var(--bo-ink)] text-[13.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-[11.5px] text-[color:var(--bo-mute)]">
          <Link to={'/login' as never} className="font-medium text-[color:var(--bo-accent)] hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
